'use client';

import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { backupSummary, createBackupPayload, restoreBackupPayload, validateImportPayload } from '@/lib/backup';
import { formatMonthKey } from '@/lib/date';
import { storageErrorMessage } from '@/lib/storageErrors';

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

export default function ExportImportPanel() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const exportData = async () => {
    try {
      const payload = await createBackupPayload();
      const file = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `licaio-sauvegarde-${formatMonthKey(new Date())}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Sauvegarde prête. Gardez ce fichier dans un endroit sûr.');
    } catch (err) {
      setMessage(storageErrorMessage('Impossible de créer la sauvegarde.', err));
    }
  };

  const chooseImportFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setPendingImport(file);
    setMessage(file ? 'Confirmez la restauration pour remplacer les données actuelles.' : null);
  };

  const cancelImport = () => {
    setPendingImport(null);
    setMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    const text = await pendingImport.text();
    try {
      const data = validateImportPayload(JSON.parse(text));
      await restoreBackupPayload(data);
      notifyUpdate();
      setPendingImport(null);
      setMessage(
        `Restauration terminée: ${backupSummary(data)} restauré(s). ${data.ignoredCount > 0 ? `${data.ignoredCount} élément(s) invalide(s) ignoré(s).` : 'Aucun élément invalide détecté.'}`
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Impossible de restaurer ce fichier. Vérifiez que c’est une sauvegarde JSON Licaio.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-brand-900/70 bg-brand-950/40 p-4">
        <p className="text-sm font-semibold text-brand-100">Vos données restent sur cet appareil.</p>
        <p className="mt-2 text-sm text-brand-200/80">
          Licaio ne les envoie sur aucun serveur. Pensez à faire une sauvegarde régulièrement pour ne pas les perdre.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <button type="button" onClick={exportData} className="primary-button w-full">
          Sauvegarder mes données
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} className="secondary-button w-full">
          Restaurer
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        onChange={chooseImportFile}
        className="hidden"
        aria-label="Choisir une sauvegarde JSON Licaio"
      />

      {pendingImport ? (
        <div className="rounded-2xl border border-amber-900/70 bg-amber-950/40 p-4" role="alertdialog" aria-label="Confirmer la restauration locale">
          <p className="text-sm font-semibold text-amber-100">Restaurer cette sauvegarde ?</p>
          <p className="mt-2 text-sm text-amber-200/80">
            Cette action remplace toutes les données locales actuelles par le contenu de {pendingImport.name}.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={confirmImport} disabled={importing} className="primary-button bg-amber-600 hover:bg-amber-700 sm:flex-1">
              {importing ? 'Restauration...' : 'Confirmer la restauration'}
            </button>
            <button type="button" onClick={cancelImport} disabled={importing} className="secondary-button sm:flex-1">
              Annuler
            </button>
          </div>
        </div>
      ) : null}

      {message ? <p className="rounded-2xl bg-slate-950 p-3 text-sm leading-6 text-slate-300" role="status">{message}</p> : null}
    </div>
  );
}
